import { takeEvery, takeLatest, put, call, select, take } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { FETCH_USERS_SUCCESS } from 'constants/ActionTypes';

import { PATH } from 'constants/RoutesConstants';

import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';
import { simpleRequest } from 'modules/app/utils';

import * as detailsActions from 'modules/audits/actions/details';
import * as actionTypes from 'modules/audits/actionTypes';
import { DETAILS_SOURCE, DRAWER_PREVIEW_SOURCE } from 'modules/audits/constants';

import { getSelectedId } from 'modules/audits/selectors/details';
import { AuditQuestionTypeEnum } from 'modules/audits/types/resources/audits.enum';
import { selectDealer } from 'modules/dealers/actions';
import { createNotification } from 'modules/toast-notifications/actions';
import { getUserGroups } from 'modules/userGroups/actions';
import { getSelectedDealerId } from 'selectors/dealerSelectors';

import { fetchUsers as fetchUsersAction } from '../../../actions/userActions';
import { downloadFile, downloadURL } from '../../../services/Utils';

function* openShareModalWorker({ payload: auditId, meta = {} }) {
  yield put(detailsActions.fetchAudit(auditId, { source: DETAILS_SOURCE, ...meta }));
}

// Registered inside `initDetailsPage` (page-scoped), so it no longer needs a `source === DETAILS_SOURCE`
// guard: while the audit detail route is active every audit fetch/save is a detail view (drawer previews
// only fire from intervention pages).
function* afterLoadAudit({ payload, meta }) {
  const { id, sections, dealer } = payload;

  yield put(detailsActions.fetchLinkedAudits(id, meta));

  if (dealer?.id) {
    // Preloads groups for the "Assigned to" select (BMWVID-27360 AC-14/15), same pattern as
    // interventions' `loadDealerInformation` in modules/interventions/sagas/details.saga.js.
    yield put(getUserGroups(dealer.id));
  }

  if (sections?.length > 0) {
    const ids = sections?.flatMap(section => {
      const users = [];

      if (section.questions?.length > 0) {
        section.questions.forEach(question => {
          switch (question.question.type) {
            case AuditQuestionTypeEnum.AUTHOR:
              users.push(question.author);
              break;
            case AuditQuestionTypeEnum.EMPLOYEE:
              users.push(question.employee);
              break;
            case AuditQuestionTypeEnum.MULTI_EMPLOYEE:
              if (question.employees) {
                users.push(...question.employees);
              }
              break;
            case AuditQuestionTypeEnum.KPI:
              if (question.responsible) {
                users.push(question.responsible);
              }
              break;
            default:
              break;
          }
        });
      }

      return users;
    });

    yield put(fetchUsersAction({ ids }));
  }
}

function* fetchUsers({ payload }) {
  const params = {
    sort: 'firstName,asc,lastName,asc',
    fields: 'id,displayName,username,roles,status,tags,firstName,lastName',
    ...payload.params,
  };

  yield put(fetchUsersAction(params, { selectId: payload.selectId }));
}

function* fetchUsersSuccess({ payload, meta }) {
  if (meta?.selectId) {
    yield put(
      detailsActions.fetchUsersSelectSuccess({
        selectId: meta.selectId,
        userIds: payload.content.map(user => user.id),
        links: payload.links,
        page: payload.page,
      })
    );
  }
}

function* fetchNextUsers({ payload }) {
  yield put(fetchUsersAction(payload.params, { selectId: payload.selectId }));
}

function* createMarker({ payload }) {
  const { request } = payload;

  try {
    const { data: marker } = yield call(restClient.request, request);
    yield put(
      detailsActions.postMarkerSuccess({
        ...payload,
        marker,
      })
    );
  } catch (errorMessage) {
    yield put(detailsActions.postMarkerFailure(errorMessage));
    yield put(
      createNotification({
        message: 'audito.model.builder.sections.section.question.error.explodedView.createMarker',
        type: 'error',
      })
    );
  }
}

function* updateMarker({ payload }) {
  const { request } = payload;

  try {
    const { data: marker } = yield call(restClient.request, request);
    yield put(
      detailsActions.patchMarkerSuccess({
        ...payload,
        marker,
      })
    );
  } catch (errorMessage) {
    yield put(detailsActions.patchMarkerFailure(errorMessage));
    yield put(
      createNotification({
        message: 'audito.model.builder.sections.section.question.error.explodedView.createMarker',
        type: 'error',
      })
    );
  }
}

function* deleteMarker({ payload }) {
  const { request, auditId, sectionId, questionId, markerId } = payload;

  try {
    yield call(restClient.request, request);
    yield put(detailsActions.deleteMarkerSuccess({ auditId, sectionId, questionId, markerId }));
  } catch (errorMessage) {
    yield put(detailsActions.deleteMarkerFailure(errorMessage));
    yield put(
      createNotification({
        message: 'audito.model.builder.sections.section.question.error.explodedView.deleteMarker',
        type: 'error',
      })
    );
  }
}

function* fetchFOUrl({ payload }) {
  const { request } = payload;
  try {
    const { data: url } = yield call(restClient.request, request);
    yield put(detailsActions.fetchFOUrlSuccess(url));
  } catch (error) {
    yield put(detailsActions.fetchFOUrlFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.audit_share_url.failure',
          defaultMessage: 'Can’t get the audit link',
        },
      })
    );
  }
}

function* auditSend({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(detailsActions.auditSendSuccess(data));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'audito.page.audit.notification.share_audit.success',
          defaultMessage: 'Audit shared',
        },
      })
    );
  } catch (error) {
    yield put(detailsActions.auditSendFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'audito.page.audit.notification.share_audit.failure',
          defaultMessage: 'Can’t share the audit',
        },
      })
    );
  }
}

function handleFileDownload({ payload }) {
  const { fileName, data } = payload;
  typeof data === 'object' && data.path
    ? downloadURL(data.path)
    : downloadFile(data, { fileName, type: 'application/pdf' });
}

function* openDrawerPreview({ payload: auditId }) {
  const { error } = yield putAndWait(detailsActions.fetchAudit(auditId, { source: DRAWER_PREVIEW_SOURCE }));
  if (error) {
    yield put(detailsActions.closeDrawerPreview());
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'audito.page.audit.notification.preview.failure',
          defaultMessage: "Can't preview the audit",
        },
      })
    );
  }
}

// Aligns the globally-selected dealer with the audit's own dealer. Lives here (page-scoped) rather than
// inline in the generic `get` saga, which also serves drawer previews from intervention pages.
function* syncSelectedDealer(audit) {
  const dealerId = yield select(getSelectedDealerId);
  if (dealerId !== audit.dealer.id) {
    yield put(selectDealer(audit.dealer.id));
  }
}

// Runs whenever the audit detail route is active (scopeToPage waits for APPLICATION_IS_READY and
// re-forks on each navigation, cancelling the previous run). The selected id comes from the URL, so
// landing on / switching between audit details fetches the right one. `afterLoadAudit` is registered
// here so it stays scoped to this page — no top-level listener, no source guard.
function* initDetailsPage() {
  yield takeLatest([actionTypes.PUT_SUCCESS, actionTypes.FETCH_AUDIT_SUCCESS], afterLoadAudit);

  const auditId = yield select(getSelectedId);
  yield put(detailsActions.fetchAudit(auditId, { source: DETAILS_SOURCE }));

  const { payload: audit } = yield take(actionTypes.FETCH_AUDIT_SUCCESS);
  yield call(syncSelectedDealer, audit);
}

export default function* detailsSaga() {
  yield scopeToPage([PATH.AUDITS_DETAILED], initDetailsPage);

  yield takeLatest(actionTypes.OPEN_SHARE_MODAL, openShareModalWorker);

  // `takeEvery` is mandatory here to handle multiple selects on one page.
  yield takeEvery(actionTypes.FETCH_USERS_SELECT_REQUEST, fetchUsers);
  yield takeEvery(FETCH_USERS_SUCCESS, fetchUsersSuccess);
  yield takeEvery(actionTypes.FETCH_NEXT_USERS_SELECT_REQUEST, fetchNextUsers);

  yield takeLatest(actionTypes.POST_MARKER_REQUEST, createMarker);
  yield takeLatest(actionTypes.PATCH_MARKER_REQUEST, updateMarker);
  yield takeLatest(actionTypes.DELETE_MARKER_REQUEST, deleteMarker);

  yield takeLatest(actionTypes.SHARE_AUDIT, auditSend);
  yield takeLatest(actionTypes.FETCH_FO_URL, fetchFOUrl);

  yield takeLatest(actionTypes.DRAWER_PREVIEW_OPEN, openDrawerPreview);

  yield takeLatest(actionTypes.PRINT_CUSTOMER_VIEW, simpleRequest, {
    callbacks: {
      success: (data, __, parentAction) =>
        detailsActions.printCustomerViewSuccess(
          { fileName: parentAction.payload.fileName, auditId: parentAction.payload.auditId, data },
          parentAction.meta
        ),
      failure: (error, meta, parentAction) =>
        detailsActions.printCustomerViewFailure(error, { ...meta, auditId: parentAction.payload.auditId }),
    },
    notifications: {
      failure: {
        id: 'http.error.message',
        defaultMessage: 'Oops! Something went wrong.',
      },
    },
  });
  yield takeLatest(actionTypes.PRINT_CUSTOMER_VIEW_SUCCESS, handleFileDownload);
}
